import { useId, useMemo, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Label } from "./label";
import { FieldContext, type FieldContextValue } from "./field-context";

interface FieldProps {
  label: ReactNode;
  required?: boolean;
  htmlFor?: string;
  error?: string | undefined;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Form field wrapper — label on top, input slot, optional inline error or
 *  hint underneath. Children rendered inside this wrapper auto-pick up
 *  `aria-describedby` (linking to the error/hint paragraph) and
 *  `aria-invalid` via FieldContext, so a screen reader hears the error
 *  message when the input is focused, not just visually. */
export function Field({ label, required, htmlFor, error, hint, className, children }: FieldProps) {
  // Stable fallback id so the label + error/hint association works even when
  // the caller doesn't pass `htmlFor`. A caller-supplied `htmlFor` always wins,
  // so existing id-based wiring is unchanged.
  const autoId = useId();
  const fieldId = htmlFor ?? autoId;
  const errorId = error ? `${fieldId}-error` : undefined;
  const hintId = hint ? `${fieldId}-hint` : undefined;

  const ctx = useMemo<FieldContextValue>(
    () => ({ fieldId, errorId, hintId, invalid: Boolean(error) }),
    [fieldId, errorId, hintId, error],
  );

  return (
    <FieldContext.Provider value={ctx}>
      <div className={cn("flex flex-col gap-1.5", className)}>
        <Label htmlFor={fieldId} required={required}>
          {label}
        </Label>
        {children}
        {error ? (
          <p id={errorId} className="text-xs font-semibold text-destructive">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
